import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Plus, Wallet, TrendingUp, TrendingDown, X, ArrowUpCircle, ArrowDownCircle,
  Trash2, Building2, Users, DollarSign, Calendar, ChevronDown, ChevronUp, FolderKanban,
  BookOpen, CheckCircle2, AlertTriangle
} from 'lucide-react';
import { useFocusEntity } from '../hooks/useFocusEntity';

const PettyCash = () => {
  const navigate = useNavigate();
  const { focusedId } = useFocusEntity();
  const [funds, setFunds] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFundModal, setShowFundModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  // Phase 7.x · P1 — Settlement state: { fund, preview }
  const [settleCtx, setSettleCtx] = useState(null);
  const [settleForm, setSettleForm] = useState({
    settlement_type: 'manual',
    settlement_target_account_id: '',
    notes: ''
  });
  const [selectedFund, setSelectedFund] = useState(null);
  const [expandedFund, setExpandedFund] = useState(null);
  
  const [fundForm, setFundForm] = useState({
    name: '',
    description: '',
    initial_balance: '',
    max_transaction_limit: '',
    employee_id: '',
    project_id: ''
  });
  
  const [txForm, setTxForm] = useState({
    fund_id: '',
    transaction_type: 'withdrawal',
    amount: '',
    employee_id: '',
    project_id: '',
    description: '',
    category: ''
  });

  const categories = [
    'Office Supplies',
    'Transportation',
    'Meals & Entertainment',
    'Utilities',
    'Maintenance',
    'Communication',
    'Miscellaneous'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [fundsRes, txRes, empRes, projRes, summaryRes, accRes, setRes, outRes] = await Promise.all([
        axiosInstance.get('/petty-cash/funds'),
        axiosInstance.get('/petty-cash/transactions'),
        axiosInstance.get('/employees'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/petty-cash/summary'),
        axiosInstance.get('/accounts'),
        axiosInstance.get('/petty-cash/settlement'),
        axiosInstance.get('/petty-cash/settlement/outstanding')
      ]);
      setFunds(fundsRes.data);
      setTransactions(txRes.data);
      setEmployees(empRes.data.filter(emp => emp.approval_status === 'approved'));
      setProjects(projRes.data.filter(p => p.status === 'active' || p.status === 'in_progress'));
      setSummary(summaryRes.data);
      setAccounts(accRes.data || []);
      setSettlements(setRes.data || []);
      setOutstanding(outRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFund = async () => {
    try {
      await axiosInstance.post('/petty-cash/funds', {
        ...fundForm,
        initial_balance: parseFloat(fundForm.initial_balance) || 0,
        max_transaction_limit: fundForm.max_transaction_limit ? parseFloat(fundForm.max_transaction_limit) : null,
        employee_id: fundForm.employee_id || null,
        project_id: fundForm.project_id || null
      });
      toast.success('Fund created successfully');
      setShowFundModal(false);
      setFundForm({ name: '', description: '', initial_balance: '', max_transaction_limit: '', employee_id: '', project_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create fund');
    }
  };

  const handleCreateTransaction = async () => {
    try {
      await axiosInstance.post('/petty-cash/transactions', {
        ...txForm,
        amount: parseFloat(txForm.amount),
        employee_id: txForm.employee_id || null,
        project_id: txForm.project_id || null
      });
      toast.success('Transaction recorded successfully');
      setShowTransactionModal(false);
      setTxForm({ fund_id: '', transaction_type: 'withdrawal', amount: '', employee_id: '', project_id: '', description: '', category: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record transaction');
    }
  };

  const handleDeleteTransaction = async (txId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    try {
      await axiosInstance.delete(`/petty-cash/transactions/${txId}`);
      toast.success('Transaction deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleDeleteFund = async (fundId) => {
    if (!window.confirm('Are you sure you want to delete this fund?')) return;
    try {
      await axiosInstance.delete(`/petty-cash/funds/${fundId}`);
      toast.success('Fund deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete fund');
    }
  };

  // Phase 7.x · P1 · Settlement Engine — open with preview.
  const openSettleModal = async (fund) => {
    try {
      const r = await axiosInstance.get(`/petty-cash/settlement/preview/${fund.id}`);
      setSettleCtx({ fund, preview: r.data });
      setSettleForm({
        settlement_type: 'manual',
        settlement_target_account_id: fund.default_expense_account_id || '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load settlement preview');
    }
  };

  const handlePostSettlement = async () => {
    if (!settleCtx) return;
    try {
      await axiosInstance.post('/petty-cash/settlement', {
        fund_id: settleCtx.fund.id,
        settlement_type: settleForm.settlement_type,
        settlement_target_account_id: settleForm.settlement_target_account_id || null,
        notes: settleForm.notes || null
      });
      toast.success('Settlement posted — fund closed and journal generated.');
      setSettleCtx(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post settlement');
    }
  };

  const openTransactionModal = (type, fund = null) => {
    if (funds.length === 0) {
      toast.error('Please create a Petty Cash Fund first via the "New Fund" button.');
      setShowFundModal(true);
      return;
    }
    setTxForm({
      fund_id: fund?.id || '',
      transaction_type: type,
      amount: '',
      employee_id: '',
      project_id: '',
      description: '',
      category: ''
    });
    setShowTransactionModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Petty Cash</h1>
          <p className="text-gray-500">Manage petty cash funds and transactions</p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="add-fund-button"
            onClick={() => setShowFundModal(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <Building2 className="w-4 h-4" />
            New Fund
          </button>
          <button
            data-testid="add-transaction-button"
            onClick={() => openTransactionModal('withdrawal')}
            className="btn-primary flex items-center gap-2"
            title={funds.length === 0
              ? "Create a Petty Cash Fund first — clicking will open the Fund dialog"
              : "Record a new petty cash transaction"}
          >
            <Plus className="w-4 h-4" />
            Record Transaction
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-5 rounded-xl text-white">
            <div className="flex items-center gap-3 mb-2">
              <Wallet className="w-6 h-6" />
              <span className="text-amber-100">Total Balance</span>
            </div>
            <p className="text-3xl font-bold">{summary.total_balance?.toLocaleString()}</p>
            <p className="text-amber-100 text-sm">SAR</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-5 rounded-xl text-white">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6" />
              <span className="text-green-100">Total Deposits</span>
            </div>
            <p className="text-3xl font-bold">{summary.total_deposits?.toLocaleString()}</p>
            <p className="text-green-100 text-sm">SAR</p>
          </div>
          
          <div className="bg-gradient-to-br from-red-500 to-red-600 p-5 rounded-xl text-white">
            <div className="flex items-center gap-3 mb-2">
              <TrendingDown className="w-6 h-6" />
              <span className="text-red-100">Total Withdrawals</span>
            </div>
            <p className="text-3xl font-bold">{summary.total_withdrawals?.toLocaleString()}</p>
            <p className="text-red-100 text-sm">SAR</p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-5 rounded-xl text-white">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6" />
              <span className="text-blue-100">Active Funds</span>
            </div>
            <p className="text-3xl font-bold">{summary.total_funds}</p>
            <p className="text-blue-100 text-sm">Funds</p>
          </div>
        </div>
      )}

      {/* Phase 7.x · P1 — Outstanding worklist (Finance view).
          Shows the funds that still hold non-zero cash and therefore
          need a Settlement before fully closing out. Click on a row to
          jump straight into the Settle modal for that fund. */}
      {outstanding.length > 0 && (
        <div
          data-testid="outstanding-panel"
          className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-indigo-600" />
              <h3 className="font-semibold text-indigo-900">Outstanding Custody · {outstanding.length} fund(s)</h3>
            </div>
            <span className="text-xs text-indigo-700">
              Total exposure: <b>{outstanding.reduce((s, o) => s + (o.outstanding_amount || 0), 0).toLocaleString()} SAR</b>
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {outstanding.map(o => {
              const fund = funds.find(f => f.id === o.fund_id);
              return (
                <button
                  type="button"
                  key={o.fund_id}
                  data-testid={`outstanding-row-${o.fund_id}`}
                  onClick={() => fund && openSettleModal(fund)}
                  className="flex items-center justify-between gap-2 p-2 bg-white border border-indigo-100 rounded-lg hover:border-indigo-300 hover:shadow-sm transition text-right"
                >
                  <div className="text-left min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{o.fund_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {o.employee_name || '—'} {o.project_name ? `· ${o.project_name}` : ''}
                    </p>
                  </div>
                  <span className={`text-sm font-bold whitespace-nowrap ${
                    o.outstanding_amount > 0 ? 'text-blue-700' : 'text-rose-700'
                  }`}>
                    {o.outstanding_amount.toLocaleString()} SAR
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Funds Grid */}
      <div className="grid grid-cols-3 gap-4">
        {funds.map(fund => (
          <div 
            key={fund.id} 
            data-testid={`petty-cash-fund-card-${fund.id}`}
            className={`bg-white rounded-xl border-2 p-5 transition-all ${
              fund.status === 'active' ? 'border-amber-200 hover:border-amber-400' : 'border-gray-200 opacity-60'
            } ${focusedId === fund.id ? 'focus-highlight' : ''}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-xs text-gray-400 font-mono">{fund.fund_no}</p>
                <h3 className="text-lg font-bold text-gray-800">{fund.name}</h3>
                {fund.description && (
                  <p className="text-sm text-gray-500">{fund.description}</p>
                )}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                fund.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {fund.status}
              </span>
            </div>
            
            <div className="bg-amber-50 rounded-lg p-4 mb-4">
              <p className="text-xs text-amber-600 mb-1">Current Balance</p>
              <p className="text-2xl font-bold text-amber-700">{fund.current_balance?.toLocaleString()} SAR</p>
              {fund.max_transaction_limit && (
                <p className="text-xs text-gray-500 mt-1">Max per transaction: {fund.max_transaction_limit?.toLocaleString()} SAR</p>
              )}
            </div>

            {/* Fund custody / project allocation chips (Fix #4b). */}
            {(fund.employee_name || fund.project_name) && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {fund.employee_name && (
                  <span
                    data-testid={`fund-custodian-chip-${fund.id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium"
                    title="عهدة الموظف"
                  >
                    <Users className="w-3 h-3" />
                    {fund.employee_name}
                  </span>
                )}
                {fund.project_name && (
                  <span
                    data-testid={`fund-project-chip-${fund.id}`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium"
                    title="المشروع المخصص"
                  >
                    <FolderKanban className="w-3 h-3" />
                    {fund.project_name}
                  </span>
                )}
              </div>
            )}
            
            <div className="flex gap-2">
              <button
                onClick={() => openTransactionModal('deposit', fund)}
                className="flex-1 py-2 px-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center justify-center gap-1 text-sm font-medium"
                disabled={fund.status === 'settled'}
              >
                <ArrowUpCircle className="w-4 h-4" />
                Deposit
              </button>
              <button
                onClick={() => openTransactionModal('withdrawal', fund)}
                className="flex-1 py-2 px-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center justify-center gap-1 text-sm font-medium"
                disabled={fund.current_balance <= 0 || fund.status === 'settled'}
              >
                <ArrowDownCircle className="w-4 h-4" />
                Withdraw
              </button>
              <button
                onClick={() => handleDeleteFund(fund.id)}
                className="py-2 px-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-red-100 hover:text-red-600"
                title="Delete Fund"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Phase 7.x · P1 — Settlement trigger. Hidden when the
                fund is already settled; otherwise shown for every
                active fund so the operator can close it (refund,
                project_close, payroll, employee_exit, manual). */}
            {fund.status !== 'settled' && (
              <button
                data-testid={`settle-fund-btn-${fund.id}`}
                onClick={() => openSettleModal(fund)}
                className="w-full mt-2 py-2 px-3 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 flex items-center justify-center gap-1.5 text-sm font-medium"
                title="تسوية رصيد العهدة وإقفال الصندوق"
              >
                <CheckCircle2 className="w-4 h-4" />
                Settle Fund
              </button>
            )}
            
            {/* Expand to see transactions */}
            <button
              onClick={() => setExpandedFund(expandedFund === fund.id ? null : fund.id)}
              className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-amber-600 flex items-center justify-center gap-1"
            >
              {expandedFund === fund.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {expandedFund === fund.id ? 'Hide Transactions' : 'View Transactions'}
            </button>
            
            {expandedFund === fund.id && (
              <div className="mt-3 border-t pt-3 max-h-48 overflow-y-auto">
                {transactions.filter(tx => tx.fund_id === fund.id).slice(0, 5).map(tx => (
                  <div key={tx.id} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div>
                      <p className="text-sm font-medium">
                        {tx.transaction_type === 'deposit' ? (
                          <span className="text-green-600">+ {tx.amount?.toLocaleString()}</span>
                        ) : (
                          <span className="text-red-600">- {tx.amount?.toLocaleString()}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{tx.description}</p>
                      {tx.employee_name && (
                        <p className="text-xs text-blue-600"><Users className="w-3 h-3 inline mr-1" />{tx.employee_name}</p>
                      )}
                      {tx.project_name && (
                        <p className="text-xs text-purple-600"><FolderKanban className="w-3 h-3 inline mr-1" />{tx.project_name}</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(tx.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
                {transactions.filter(tx => tx.fund_id === fund.id).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">No transactions</p>
                )}
              </div>
            )}
          </div>
        ))}
        
        {funds.length === 0 && (
          <div className="col-span-3 text-center py-12 bg-gray-50 rounded-xl">
            <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No petty cash funds yet</p>
            <button
              onClick={() => setShowFundModal(true)}
              className="mt-4 btn-primary"
            >
              Create Your First Fund
            </button>
          </div>
        )}
      </div>

      {/* Recent Transactions Table */}
      {transactions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-800">Recent Transactions</h2>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Transaction #</th>
                <th>Date</th>
                <th>Fund</th>
                <th>Type</th>
                <th>Employee</th>
                <th>Project</th>
                <th>Category</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Balance</th>
                <th>Journal</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.slice(0, 20).map(tx => (
                <tr
                  key={tx.id}
                  data-testid={`petty-cash-tx-row-${tx.id}`}
                  className={
                    (focusedId === tx.id || focusedId === tx.fund_id)
                      ? 'focus-highlight'
                      : ''
                  }
                >
                  <td className="font-mono text-sm text-amber-600">{tx.transaction_no}</td>
                  <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                  <td>{tx.fund_name}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      tx.transaction_type === 'deposit' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {tx.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                    </span>
                  </td>
                  <td>
                    {tx.employee_name ? (
                      <span className="text-blue-600 text-sm">{tx.employee_name}</span>
                    ) : '-'}
                  </td>
                  <td>
                    {tx.project_name ? (
                      <span className="text-purple-600 text-sm">{tx.project_name}</span>
                    ) : '-'}
                  </td>
                  <td>{tx.category || '-'}</td>
                  <td className="max-w-xs truncate">{tx.description}</td>
                  <td className={`font-semibold ${
                    tx.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {tx.transaction_type === 'deposit' ? '+' : '-'}{tx.amount?.toLocaleString()} SAR
                  </td>
                  <td className="font-medium">{tx.balance_after?.toLocaleString()} SAR</td>
                  <td>
                    {tx.journal_id ? (
                      <button
                        type="button"
                        data-testid={`open-petty-cash-journal-${tx.id}`}
                        title={`القيد المرتبط · ${tx.journal_id.slice(0, 12)}…`}
                        onClick={() => navigate(`/journal?focus=${tx.journal_id}`)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors text-xs font-medium border border-indigo-200"
                      >
                        <BookOpen className="w-3.5 h-3.5" />
                        افتح القيد
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteTransaction(tx.id)}
                      className="btn-icon delete"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Fund Modal */}
      {showFundModal && (
        <div className="modal-overlay" onClick={() => setShowFundModal(false)}>
          <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Create New Fund</h3>
              <button onClick={() => setShowFundModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(ev) => { ev.preventDefault(); handleCreateFund(); }} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fund Name *</label>
                <input
                  type="text"
                  value={fundForm.name}
                  onChange={e => setFundForm({...fundForm, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Main Office Petty Cash"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={fundForm.description}
                  onChange={e => setFundForm({...fundForm, description: e.target.value})}
                  className="form-input"
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Initial Balance (SAR)</label>
                <input
                  type="number"
                  value={fundForm.initial_balance}
                  onChange={e => setFundForm({...fundForm, initial_balance: e.target.value})}
                  className="form-input"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Transaction Limit (SAR)</label>
                <input
                  type="number"
                  value={fundForm.max_transaction_limit}
                  onChange={e => setFundForm({...fundForm, max_transaction_limit: e.target.value})}
                  className="form-input"
                  placeholder="Optional - leave empty for no limit"
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Phase 7.x · Fix #4b — Custodian (Employee) + Project allocation.
                  Both optional: blank ⇒ fund is a general office cashbox.
                  Populated ⇒ fund becomes a tracked custody for that
                  employee and/or project, surfacing in their dashboards. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-dashed border-amber-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5 text-blue-500" />
                    Custodian (Employee)
                  </label>
                  <select
                    data-testid="fund-employee-select"
                    value={fundForm.employee_id}
                    onChange={e => setFundForm({...fundForm, employee_id: e.target.value})}
                    className="form-select"
                  >
                    <option value="">— None (general fund) —</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">العهدة المسؤول عنها هذا الموظف.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <FolderKanban className="w-3.5 h-3.5 text-purple-500" />
                    Project
                  </label>
                  <select
                    data-testid="fund-project-select"
                    value={fundForm.project_id}
                    onChange={e => setFundForm({...fundForm, project_id: e.target.value})}
                    className="form-select"
                  >
                    <option value="">— None (unallocated) —</option>
                    {projects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">المشروع الذي تُخصَّص له مصروفات هذا الصندوق.</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1" data-testid="submit-create-fund-btn">Create Fund</button>
                <button type="button" onClick={() => setShowFundModal(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      {showTransactionModal && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {txForm.transaction_type === 'deposit' ? 'Add Deposit' : 'Record Withdrawal'}
              </h3>
              <button onClick={() => setShowTransactionModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={(ev) => { ev.preventDefault(); handleCreateTransaction(); }} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fund *</label>
                <select
                  value={txForm.fund_id}
                  onChange={e => setTxForm({...txForm, fund_id: e.target.value})}
                  className="form-select"
                  required
                >
                  <option value="">Select Fund</option>
                  {funds.filter(f => f.status === 'active').map(f => (
                    <option key={f.id} value={f.id}>
                      {f.name} (Balance: {f.current_balance?.toLocaleString()} SAR)
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Type *</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setTxForm({...txForm, transaction_type: 'deposit'})}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 ${
                      txForm.transaction_type === 'deposit' 
                        ? 'border-green-500 bg-green-50 text-green-700' 
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <ArrowUpCircle className="w-4 h-4" />
                    Deposit
                  </button>
                  <button
                    type="button"
                    onClick={() => setTxForm({...txForm, transaction_type: 'withdrawal'})}
                    className={`flex-1 py-2 px-4 rounded-lg border-2 flex items-center justify-center gap-2 ${
                      txForm.transaction_type === 'withdrawal' 
                        ? 'border-red-500 bg-red-50 text-red-700' 
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    <ArrowDownCircle className="w-4 h-4" />
                    Withdrawal
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (SAR) *</label>
                <input
                  type="number"
                  value={txForm.amount}
                  onChange={e => setTxForm({...txForm, amount: e.target.value})}
                  className="form-input"
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  required
                />
              </div>
              
              {txForm.transaction_type === 'withdrawal' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                    <select
                      value={txForm.employee_id}
                      onChange={e => setTxForm({...txForm, employee_id: e.target.value})}
                      className="form-select"
                    >
                      <option value="">Select Employee (Optional)</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
                    <select
                      value={txForm.project_id}
                      onChange={e => setTxForm({...txForm, project_id: e.target.value})}
                      className="form-select"
                    >
                      <option value="">Select Project (Optional)</option>
                      {projects.map(proj => (
                        <option key={proj.id} value={proj.id}>{proj.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={txForm.category}
                      onChange={e => setTxForm({...txForm, category: e.target.value})}
                      className="form-select"
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={txForm.description}
                  onChange={e => setTxForm({...txForm, description: e.target.value})}
                  className="form-input"
                  rows={2}
                  placeholder="Describe the transaction..."
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button 
                  type="submit" 
                  className={`flex-1 ${txForm.transaction_type === 'deposit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white py-2 px-4 rounded-lg font-medium`}
                >
                  {txForm.transaction_type === 'deposit' ? 'Add Deposit' : 'Record Withdrawal'}
                </button>
                <button type="button" onClick={() => setShowTransactionModal(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Phase 7.x · P1 — Petty Cash Settlement Modal.
          Reconciliation only: shows the live preview (advanced / spent /
          outstanding), lets the user pick the settlement type and the
          GL account that absorbs the offsetting journal line. Posts
          via /petty-cash/settlement which writes the journal, emits
          PETTY_CASH_SETTLEMENT_POSTED + CASH_LEDGER_TOUCH, zeroes the
          fund balance, and marks status='settled'. */}
      {settleCtx && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4" onClick={() => setSettleCtx(null)}>
          <div
            data-testid="settle-modal"
            className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-bold text-gray-800">تسوية العهدة · Settle Petty Cash</h3>
              </div>
              <button onClick={() => setSettleCtx(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Fund header */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-xs text-amber-700 font-mono">{settleCtx.fund.fund_no}</p>
                <p className="font-semibold text-amber-900">{settleCtx.fund.name}</p>
                {(settleCtx.preview.employee_name || settleCtx.preview.project_name) && (
                  <p className="text-xs text-gray-600 mt-1">
                    {settleCtx.preview.employee_name ? `Custodian: ${settleCtx.preview.employee_name}` : ''}
                    {settleCtx.preview.employee_name && settleCtx.preview.project_name ? ' · ' : ''}
                    {settleCtx.preview.project_name ? `Project: ${settleCtx.preview.project_name}` : ''}
                  </p>
                )}
              </div>

              {/* Preview numbers */}
              <div data-testid="settle-preview" className="grid grid-cols-3 gap-2 text-center text-sm">
                <div className="bg-green-50 border border-green-100 rounded-lg p-2">
                  <p className="text-xs text-green-700">Total Advanced</p>
                  <p className="font-bold text-green-800">{settleCtx.preview.total_advanced.toLocaleString()} SAR</p>
                </div>
                <div className="bg-red-50 border border-red-100 rounded-lg p-2">
                  <p className="text-xs text-red-700">Total Spent</p>
                  <p className="font-bold text-red-800">{settleCtx.preview.total_spent.toLocaleString()} SAR</p>
                </div>
                <div className={`border rounded-lg p-2 ${
                  settleCtx.preview.settlement_status === 'refund_due'
                    ? 'bg-blue-50 border-blue-100'
                    : settleCtx.preview.settlement_status === 'claim_due'
                      ? 'bg-rose-50 border-rose-100'
                      : 'bg-gray-50 border-gray-100'
                }`}>
                  <p className="text-xs text-gray-700">Outstanding</p>
                  <p className={`font-bold ${
                    settleCtx.preview.outstanding_amount > 0 ? 'text-blue-700' :
                    settleCtx.preview.outstanding_amount < 0 ? 'text-rose-700' : 'text-gray-700'
                  }`}>
                    {settleCtx.preview.outstanding_amount.toLocaleString()} SAR
                  </p>
                </div>
              </div>

              {/* Status banner */}
              <div className={`flex items-start gap-2 text-sm p-3 rounded-lg border ${
                settleCtx.preview.settlement_status === 'balanced'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : settleCtx.preview.settlement_status === 'refund_due'
                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                    : 'bg-rose-50 border-rose-200 text-rose-800'
              }`}>
                {settleCtx.preview.settlement_status === 'balanced' ? (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  {settleCtx.preview.settlement_status === 'balanced' && (
                    <span>الصندوق متوازن — يمكن إقفاله دون قيد مالي.</span>
                  )}
                  {settleCtx.preview.settlement_status === 'refund_due' && (
                    <span>مطلوب استرجاع <b>{settleCtx.preview.outstanding_amount.toLocaleString()} SAR</b> من العهدة وإقفال الصندوق.</span>
                  )}
                  {settleCtx.preview.settlement_status === 'claim_due' && (
                    <span>الصندوق منكشف بمبلغ <b>{Math.abs(settleCtx.preview.outstanding_amount).toLocaleString()} SAR</b> — مطالبة الموظف أو خصم من الراتب.</span>
                  )}
                </div>
              </div>

              {/* Settlement type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Type *</label>
                <select
                  data-testid="settle-type-select"
                  value={settleForm.settlement_type}
                  onChange={(e) => setSettleForm({ ...settleForm, settlement_type: e.target.value })}
                  className="w-full form-select"
                >
                  <option value="manual">Manual</option>
                  <option value="project_close">Project Close</option>
                  <option value="payroll">Payroll</option>
                  <option value="employee_exit">Employee Exit</option>
                </select>
              </div>

              {/* Target account (only when journal needed) */}
              {settleCtx.preview.settlement_status !== 'balanced' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Settlement Target Account *
                    <span className="text-xs text-gray-500 font-normal mr-1">
                      (الحساب الذي يستقبل القيد المقابل)
                    </span>
                  </label>
                  <select
                    data-testid="settle-target-select"
                    value={settleForm.settlement_target_account_id}
                    onChange={(e) => setSettleForm({ ...settleForm, settlement_target_account_id: e.target.value })}
                    className="w-full form-select"
                  >
                    <option value="">— اختر حساب —</option>
                    {accounts.filter(a => a.status !== 'inactive').map(a => (
                      <option key={a.id} value={a.id}>
                        {a.account_code ? `${a.account_code} · ` : ''}{a.name} ({a.account_type})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  data-testid="settle-notes"
                  value={settleForm.notes}
                  onChange={(e) => setSettleForm({ ...settleForm, notes: e.target.value })}
                  className="w-full form-input"
                  rows={2}
                  placeholder="سبب التسوية / تفاصيل إضافية..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  data-testid="confirm-settle-btn"
                  onClick={handlePostSettlement}
                  disabled={
                    settleCtx.preview.settlement_status !== 'balanced' &&
                    !settleForm.settlement_target_account_id
                  }
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Post Settlement
                </button>
                <button onClick={() => setSettleCtx(null)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PettyCash;
